import { Select } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { selectOptionCurrency, selectOptionLegsProperties } from '@/store/option/selectors/optionSelectors.ts';
import type { LegId, PremiumCategory } from '@/store/option/state.ts';
import { allPriceTypes } from '@/types/common.ts';

type PremiumTypeProps = {
  tileId: string;
};

export function PriceType({ tileId }: PremiumTypeProps) {
  const { formatMessage } = useIntl();
  const [checkOption] = useCheckOption();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const legId: LegId = `${tileId}/0`;
  const { currency1, currency2, premiumType, premiumTypeCurrencyIndex, isMultileg } = useOptionSelector((option) => {
    const legProperties = selectOptionLegsProperties(option, legId);
    const premiumType = option.properties.premiumType;
    const { currency1, currency2 } = selectOptionCurrency(option);

    return {
      optionType: legProperties?.type,
      currency1,
      currency2,
      premiumType,
      premiumTypeCurrencyIndex: premiumType.premiumCurrency,
      isMultileg: option.properties.legs.length > 1 && option.type === 'Vanilla',
    };
  });

  function handlePremiumCategoryChange(premiumCategory: PremiumCategory) {
    sgwtWebAnalytics?.trackEvent('Option', 'Premium type changed', `${premiumCategory} ${premiumType.premiumCurrency}`);
    checkOption({
      tileId,
      properties: {
        premiumType: {
          premiumCategory,
          premiumCurrency: premiumType.premiumCurrency,
        },
      },
    });
  }

  function handlePremiumTypeCurrencyChange() {
    sgwtWebAnalytics?.trackEvent('Option', 'Premium type currency changed');
    checkOption({
      tileId,
      properties: {
        premiumType: {
          premiumCategory: premiumType.premiumCategory,
          premiumCurrency: premiumType.premiumCurrency === 1 ? 2 : 1,
        },
      },
    });
  }

  const priceTypesWithoutVolatility = allPriceTypes.filter((type) => type !== 'VOLATILITY');
  const availablePriceTypes = isMultileg ? priceTypesWithoutVolatility : allPriceTypes;
  return (
    <OptionFormGroup label={<FormattedMessage id="option.fields.premiumType" />}>
      <Select<PremiumCategory>
        data-e2e="premium-type"
        onChange={(premiumCategory) => handlePremiumCategoryChange(premiumCategory)}
        className="form-select-sm"
        selectedItem={premiumType.premiumCategory}
        itemToString={(premiumCategory) => formatMessage({ id: `option.fields.premiumType.values.${premiumCategory}` })}
        itemsAsObjects={availablePriceTypes}
      />
      <button
        type="button"
        data-e2e="premium-type-currency"
        onClick={() => {
          handlePremiumTypeCurrencyChange();
        }}
        className={`${premiumTypeCurrencyIndex === 2 ? 'btn-warning' : 'btn-discreet-primary'} btn btn-sm`}
      >
        {premiumTypeCurrencyIndex === 1 ? currency1 : currency2}
      </button>
    </OptionFormGroup>
  );
}
